import { Collapse, Form, Input, Select, SelectProps } from "antd";
import { useParams } from "react-router-dom";
import {
  botExtraDataInitialValues,
  TTelegramBotDialogueRow,
} from "@lib/types/TelegramChatBot";
import { GET_LANGUAGE_FROM_CODE } from "@lib/constants/Language";
import { enum2list } from "@lib/helpers/OptionsHelper";
import { DropResult } from "react-beautiful-dnd";
import {
  _BotOperationsEnum,
  AuthBotOperationsEnum,
  OptionType,
} from "@lib/constants/Enums";
import { ICollapseItem } from "../definition";
import BotMessageForm from "../collapse-form/BotMessageForm";
import { useMutation } from "@tanstack/react-query";
import {
  createOneTelegramBotDialogue,
  updateOneTelegramBotDialogue,
} from "@lib/api/telegramBotDialogue";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import CollapseItemOptions from "../collapse-item-options";
import RULES from "@lib/schemas/general";

const { Panel } = Collapse;

interface ICollapseItemFormProps {
  mode: "add" | "edit";
  dialogue?: TTelegramBotDialogueRow;
  parentId?: string;
  closeModal?: (e?) => void;
  refetchDialogue?: () => void;
  requireAuth?: boolean;
}

const CollapseItemForm = ({
  mode,
  dialogue,
  parentId,
  closeModal,
  refetchDialogue,
  requireAuth,
}: ICollapseItemFormProps) => {
  const { messages } = useIntl();
  const { configId } = useParams();
  const [form] = Form.useForm<TTelegramBotDialogueRow>();

  if (mode === "edit" && dialogue?.["extraData"]) {
    dialogue["extraData"][_BotOperationsEnum.BOT_MESSAGE] = {
      ...botExtraDataInitialValues?.[_BotOperationsEnum.BOT_MESSAGE],
      ...dialogue?.extraData?.[_BotOperationsEnum.BOT_MESSAGE],
    };
  }

  const languages = enum2list(GET_LANGUAGE_FROM_CODE, true);

  const options: SelectProps["options"] = languages.map((lang) => ({
    value: lang.label.toLowerCase(),
    label: lang.value,
  }));

  const {
    mutate: createTelegramBotDialogue,
    isPending: isCreateTelegramBotDialogueLoading,
  } = useMutation({
    mutationKey: ["createTelegramBotDialogue"],
    mutationFn: (values: TTelegramBotDialogueRow) =>
      createOneTelegramBotDialogue(values),
    onSuccess: () => {
      form.resetFields();
      refetchDialogue?.();
      closeModal?.();
      toast.success(messages["action.success"] as string);
    },
  });

  const {
    mutate: editTelegramBotDialogue,
    isPending: isEditTelegramBotDialogueLoading,
  } = useMutation({
    mutationKey: ["editTelegramBotDialogue"],
    mutationFn: (values: TTelegramBotDialogueRow) =>
      updateOneTelegramBotDialogue(values),
    onSuccess: () => {
      form.resetFields();
      refetchDialogue?.();
      closeModal?.();
      toast.success(messages["action.success"] as string);
    },
  });

  const onFinish = (values: TTelegramBotDialogueRow) => {
    const payload: TTelegramBotDialogueRow = {
      ...values,
      requireAuth,
    };

    if (mode === "edit" && dialogue) {
      payload.id = dialogue.id;
      if (dialogue?.parentId) {
        payload.parentId = dialogue.parentId;
      }
      if (dialogue?.configId) {
        payload.configId = dialogue.configId;
      }
      editTelegramBotDialogue(payload);
    } else {
      if (parentId) {
        payload.parent = {
          id: parentId,
        };
      }
      if (configId) {
        payload.config = {
          id: configId,
        };
      }
      createTelegramBotDialogue(payload);
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const options = form.getFieldValue("options");
    const [removed] = options.splice(source.index, 1);
    options.splice(destination.index, 0, removed);

    form.setFieldValue("options", options);
  };

  return (
    <>
      {mode === "add" && (
        <div
          style={{
            display: "flex",
            alignItems: "end",
            justifyContent: "end",
            marginBottom: "20px",
          }}
        >
          <ActionButton onClick={closeModal} type="close" />
        </div>
      )}
      <Form
        name={`${mode}-dialogue-${dialogue?.id || configId}-${parentId}`}
        wrapperCol={{ lg: 16 }}
        labelCol={{ md: 4 }}
        style={{ padding: 16, width: "100%" }}
        autoComplete="off"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onFinish={onFinish}
        form={form}
        initialValues={mode === "edit" ? dialogue : undefined}
      >
        <Form.Item
          label={messages["common.title"] as string}
          name="title"
          rules={[RULES.required]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
        </Form.Item>
        {(parentId || dialogue?.parentId) && (
          <Form.Item
            label={messages["common.label"] as string}
            name="label"
            rules={[RULES.required]}
          >
            <Input />
          </Form.Item>
        )}
        {!parentId && !dialogue?.parentId && (
          <Form.Item
            label={messages["common.language"] as string}
            name="language"
            rules={[RULES.required]}
          >
            <Select
              showSearch
              placeholder={messages["common.selectOne"] as string}
              filterOption={true}
              aria-autocomplete="none"
              optionLabelProp="label"
              optionFilterProp="label"
              options={options}
            />
          </Form.Item>
        )}

        <CollapseItemOptions
          dialogue={mode === "edit" ? dialogue : undefined}
          onDragEnd={mode === "add" ? onDragEnd : undefined}
        />

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            currentValues?.options?.some(
              (option) =>
                Object.keys(AuthBotOperationsEnum).includes(
                  option?.[0]?.callback_data,
                ) && Object.keys(OptionType).includes(option?.[0]?.optionType),
            )
          }
        >
          {({ getFieldValue }) => {
            const options = getFieldValue("options");
            const isDepositSelected = options?.some(
              (option) =>
                option?.[0]?.callback_data === AuthBotOperationsEnum.DEPOSIT &&
                option?.[0]?.optionType === OptionType.callback_data,
            );
            const isCheckBalanceSelected = options?.some(
              (option) =>
                option?.[0]?.callback_data ===
                  AuthBotOperationsEnum.CHECK_BALANCE &&
                option?.[0]?.optionType === OptionType.callback_data,
            );
            const isWithdrawSelected = options?.some(
              (option) =>
                option?.[0]?.callback_data === AuthBotOperationsEnum.WITHDRAW &&
                option?.[0]?.optionType === OptionType.callback_data,
            );
            const isChatWithAgentSelected = options?.some(
              (option) =>
                option?.[0]?.callback_data ===
                  AuthBotOperationsEnum.CHAT_WITH_AGENT &&
                option?.[0]?.optionType === OptionType.callback_data,
            );

            const props = {
              isDepositSelected,
              isCheckBalanceSelected,
              isWithdrawSelected,
              isChatWithAgentSelected,
            };

            const botOperationsItems: ICollapseItem[] = [
              {
                key: "bot_message",
                label: messages["TELEGRAM.BOT_MESSAGE_FORM"] as string,
                children: <BotMessageForm {...props} />,
              },
            ];

            return isDepositSelected ||
              isCheckBalanceSelected ||
              isWithdrawSelected ||
              isChatWithAgentSelected ? (
              <>
                <Collapse defaultActiveKey={["bot_message"]}>
                  {botOperationsItems?.map((item) => (
                    <Panel header={item.label} key={item.key}>
                      {item.children}
                    </Panel>
                  ))}
                </Collapse>
              </>
            ) : null;
          }}
        </Form.Item>

        <div style={{ display: "flex", justifyContent: "end", gap: 10 }}>
          <ActionButton
            type="cancel"
            onClick={(e) => {
              e.stopPropagation();
              closeModal?.();
            }}
          />
          <ActionButton
            type="submit"
            loading={
              mode === "edit"
                ? isEditTelegramBotDialogueLoading
                : isCreateTelegramBotDialogueLoading
            }
          />
        </div>
      </Form>
    </>
  );
};

export default CollapseItemForm;
